import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import HomeConcept from "@/pages/HomeConcept";
import Dealerships from "@/pages/Dealerships";
import DealershipThanks from "@/pages/DealershipThanks";
import AdminCRM from "@/pages/AdminCRM";
import GetDemo from "@/pages/GetDemo";
import ProspectScore from "@/pages/ProspectScore";
import RequestQuote from "@/pages/RequestQuote";
import AdminQuotes from "@/pages/AdminQuotes";
import AdminContacts from "@/pages/AdminContacts";
import AdminLeads from "@/pages/AdminLeads";
import AdminProspects from "@/pages/AdminProspects";
import Pricing from "@/pages/Pricing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminLegal from "@/pages/AdminLegal";
import CookieConsent from "@/components/CookieConsent";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import AssetDetail from "@/pages/AssetDetail";
import Locations from "@/pages/Locations";
import QRCodes from "@/pages/QRCodes";
import Movement from "@/pages/Movement";
import Reports from "@/pages/Reports";
import Insights from "@/pages/Insights";
import Corporate from "@/pages/Corporate";
import CorporateVehicles from "@/pages/CorporateVehicles";
import AdminGroups from "@/pages/AdminGroups";
import Users from "@/pages/Users";
import CompanySettings from "@/pages/CompanySettings";
import Billing from "@/pages/Billing";
import Scan from "@/pages/Scan";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminDemoRequests from "@/pages/AdminDemoRequests";
import AdminTraffic from "@/pages/AdminTraffic";
import AdminVideos from "@/pages/AdminVideos";
import Videos from "@/pages/Videos";
import Analytics from "@/components/Analytics";
import PaymentResult from "@/pages/PaymentResult";

const Wrap = ({ children }) => (
  <ProtectedRoute>
    <DashboardLayout>{children}</DashboardLayout>
  </ProtectedRoute>
);

function AppRoutes() {
  const location = useLocation();
  // Process Google OAuth callback FIRST (synchronously) before any route/auth check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeConcept />} />
      <Route path="/home-v1" element={<Home />} />
      <Route path="/platform" element={<Landing />} />
      <Route path="/preview-home" element={<HomeConcept />} />
      <Route path="/get-demo" element={<GetDemo />} />
      <Route path="/dealerships" element={<Dealerships />} />
      <Route path="/dealerships/thank-you" element={<DealershipThanks />} />
      <Route path="/prospect-score" element={<ProspectScore />} />
      <Route path="/request-quote" element={<RequestQuote />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/videos" element={<Videos />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/scan/:token" element={<Scan />} />

      <Route path="/app/dashboard" element={<Wrap><Dashboard /></Wrap>} />
      <Route path="/app/inventory" element={<Wrap><Inventory /></Wrap>} />
      <Route path="/app/assets/:id" element={<Wrap><AssetDetail /></Wrap>} />
      <Route path="/app/locations" element={<Wrap><Locations /></Wrap>} />
      <Route path="/app/qr-codes" element={<Wrap><QRCodes /></Wrap>} />
      <Route path="/app/movement" element={<Wrap><Movement /></Wrap>} />
      <Route path="/app/reports" element={<Wrap><Reports /></Wrap>} />
      <Route path="/app/insights" element={<Wrap><Insights /></Wrap>} />
      <Route path="/app/corporate" element={<Wrap><Corporate /></Wrap>} />
      <Route path="/app/corporate/vehicles" element={<Wrap><CorporateVehicles /></Wrap>} />
      <Route path="/app/admin/groups" element={<Wrap><AdminGroups /></Wrap>} />
      <Route path="/app/users" element={<Wrap><Users /></Wrap>} />
      <Route path="/app/settings" element={<Wrap><CompanySettings /></Wrap>} />
      <Route path="/app/billing" element={<Wrap><Billing /></Wrap>} />
      <Route path="/app/admin" element={<Wrap><AdminCompanies /></Wrap>} />
      <Route path="/app/admin/demo-requests" element={<Wrap><AdminDemoRequests /></Wrap>} />
      <Route path="/app/admin/prospects" element={<Wrap><AdminProspects /></Wrap>} />
      <Route path="/app/admin/quotes" element={<Wrap><AdminQuotes /></Wrap>} />
      <Route path="/app/admin/contacts" element={<Wrap><AdminContacts /></Wrap>} />
      <Route path="/app/admin/leads" element={<Wrap><AdminLeads /></Wrap>} />
      <Route path="/app/admin/crm" element={<Wrap><AdminCRM /></Wrap>} />
      <Route path="/app/admin/legal" element={<Wrap><AdminLegal /></Wrap>} />
      <Route path="/app/admin/traffic" element={<Wrap><AdminTraffic /></Wrap>} />
      <Route path="/app/admin/videos" element={<Wrap><AdminVideos /></Wrap>} />
      <Route path="/app/scan" element={<Wrap><Scan /></Wrap>} />

      <Route path="/payment/success" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Analytics />
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
